import { View, DataView, TableView, ContextMenu, Dialog, LogList } from 'web-mojo';
import { Device } from '@portal/models/Device.js';
import { DeviceHistoryList } from '@portal/models/DeviceHistory.js';
import DeviceFirmwareView from '../firmware/views/DeviceFirmwareView.js';

class DeviceView extends View {
    constructor(options = {}) {
        super({
            className: 'device-view',
            viewDialogOptions: {
                size: "md"
            },
            ...options
        });
        this._fabPending = false;
    }

    async getTemplate() {
        return `
        <style>
            /* Widen the device dialog ~30% over the Bootstrap "md" default (500px → 650px)
               so wide tab content (e.g. the Firmware update-history table) has more room;
               the table still scrolls horizontally within its own responsive wrapper. */
            .modal-dialog:has(.device-view) { max-width: 650px; }

            /* ═══════════════════════════════════════════════════════
               CARD SHELL
            ═══════════════════════════════════════════════════════ */
            .bc {
                font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
                transition: background 0.4s ease;
                position: relative;
                -webkit-font-smoothing: antialiased;
            }
            .bc.secured  { background: transparent; }
            .bc.unlocked { background: #f0fdf4; }
            .bc.open     { background: #fefce8; }
            .bc.offline  { background: #fafafa; }

            /* ═══════════════════════════════════════════════════════
               HEADER ROW — name, dot, uuid, menu
            ═══════════════════════════════════════════════════════ */
            .bc-hd {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 1rem 1.125rem 0.75rem;
                border-bottom: 1px solid rgba(0,0,0,0.05);
            }
            .bc-hd-left { display: flex; align-items: center; gap: 0.5rem; }
            .bc-name { font-size: 0.825rem; font-weight: 600; color: #27272a; letter-spacing: -0.01em; }
            .bc-uuid { font-size: 0.6rem; font-family: 'SF Mono','Fira Code',monospace; color: #a1a1aa; letter-spacing: 0.5px; }
            .bc-dot-on {
                width: 7px; height: 7px; border-radius: 50%; background: #22c55e; flex-shrink: 0;
                box-shadow: 0 0 6px rgba(34,197,94,0.45); animation: bc-dotpulse 2.5s ease infinite;
            }
            .bc-dot-off { width: 7px; height: 7px; border-radius: 50%; background: #d4d4d8; flex-shrink: 0; }
            @keyframes bc-dotpulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

            /* ═══════════════════════════════════════════════════════
               HERO — ghost, FAB, state word, sub-line, pips
            ═══════════════════════════════════════════════════════ */
            .bc-hero {
                padding: 1.25rem 1.125rem 1.125rem;
                position: relative;
                min-height: 120px;
            }
            .bc-ghost {
                position: absolute;
                right: 0.75rem;
                bottom: -0.5rem;
                font-size: 6.5rem;
                line-height: 1;
                pointer-events: none;
                transition: color 0.4s, transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s;
            }
            .bc.secured  .bc-ghost { color: #dc2626; opacity: 0.055; }
            .bc.unlocked .bc-ghost { color: #16a34a; opacity: 0.06;  transform: rotate(14deg); }
            .bc.open     .bc-ghost { color: #d97706; opacity: 0.06;  transform: rotate(7deg); }
            .bc.offline  .bc-ghost { color: #a1a1aa; opacity: 0.035; }

            .bc-fab {
                position: absolute;
                top: 1.125rem;
                right: 1.125rem;
                width: 46px; height: 46px;
                border-radius: 50%;
                border: none;
                cursor: pointer;
                display: flex; align-items: center; justify-content: center;
                font-size: 1.1rem;
                box-shadow: 0 4px 14px rgba(0,0,0,0.14);
                transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
                outline: none;
                z-index: 1;
            }
            .bc-fab:active { transform: scale(0.87); }
            .bc.secured  .bc-fab { background: #dc2626; color: #fff; }
            .bc.unlocked .bc-fab { background: #e4e4e7; color: #3f3f46; }
            .bc.open     .bc-fab { background: #d97706; color: #fff; }
            .bc.offline  .bc-fab { background: #f4f4f5; color: #d4d4d8; pointer-events: none; box-shadow: none; }

            .bc .table {
                --bs-table-bg: transparent;
            }

            .bc-state {
                font-size: 2.5rem;
                font-weight: 900;
                letter-spacing: -0.05em;
                line-height: 1;
                margin-bottom: 0.3rem;
                padding-right: 3.5rem;
                transition: color 0.4s;
            }
            .bc.secured  .bc-state { color: #dc2626; }
            .bc.unlocked .bc-state { color: #16a34a; }
            .bc.open     .bc-state { color: #ca8a04; }
            .bc.offline  .bc-state { color: #a1a1aa; }

            .bc-sub { font-size: 0.72rem; color: #a1a1aa; margin-bottom: 0.875rem; }

            .bc-pips { display: flex; gap: 0.35rem; flex-wrap: wrap; }
            .bc-pip {
                display: flex; align-items: center; gap: 5px;
                padding: 4px 10px; border-radius: 20px;
                font-size: 0.62rem; font-weight: 600;
                border: 1px solid transparent;
                transition: all 0.3s;
            }
            .bc-pip .pd { width: 5px; height: 5px; border-radius: 50%; }
            .bc-pip.bon  { background: rgba(220,38,38,0.08);  color: #b91c1c; border-color: rgba(220,38,38,0.15); }
            .bc-pip.bon  .pd { background: #ef4444; }
            .bc-pip.boff { background: rgba(22,163,74,0.08);  color: #15803d; border-color: rgba(22,163,74,0.15); }
            .bc-pip.boff .pd { background: #22c55e; }
            .bc-pip.lc   { background: rgba(0,0,0,0.04);      color: #71717a; border-color: rgba(0,0,0,0.07); }
            .bc-pip.lc   .pd { background: #a1a1aa; }
            .bc-pip.lo   { background: rgba(217,119,6,0.08);  color: #92400e; border-color: rgba(217,119,6,0.15); }
            .bc-pip.lo   .pd { background: #f59e0b; }
            .bc-pip.ble-on  { background: rgba(59,130,246,0.08);  color: #1d4ed8; border-color: rgba(59,130,246,0.15); cursor: pointer; }
            .bc-pip.ble-on  .pd { background: #3b82f6; }
            .bc-pip.ble-off { background: rgba(0,0,0,0.04);       color: #a1a1aa; border-color: rgba(0,0,0,0.07); cursor: pointer; }
            .bc-pip.ble-off .pd { background: #d4d4d8; }
            .bc-pip.wifi-on  { background: rgba(22,163,74,0.08);  color: #15803d; border-color: rgba(22,163,74,0.15); cursor: pointer; }
            .bc-pip.wifi-on  .pd { background: #22c55e; }
            .bc-pip.wifi-off { background: rgba(0,0,0,0.04);      color: #a1a1aa; border-color: rgba(0,0,0,0.07); cursor: pointer; }
            .bc-pip.wifi-off .pd { background: #d4d4d8; }
            .bc-pip[data-action]:hover { filter: brightness(0.95); }

            .bc-hero.off { pointer-events: none; }
            .bc-hero.off .bc-state,
            .bc-hero.off .bc-sub { opacity: 0.45; }

            /* ═══════════════════════════════════════════════════════
               TAB NAV — Activity / Details / Logs
            ═══════════════════════════════════════════════════════ */
            .bc-tabs {
                display: flex;
                gap: 0.25rem;
                padding: 0.625rem 1.125rem;
                border-top: 1px solid rgba(0,0,0,0.05);
                border-bottom: 1px solid rgba(0,0,0,0.05);
            }
            .bc-tab {
                flex: 1;
                padding: 0.45rem 0;
                border: none;
                background: none;
                cursor: pointer;
                font-size: 0.72rem;
                font-weight: 600;
                color: #a1a1aa;
                border-radius: 8px;
                transition: background 0.2s ease, color 0.2s ease;
                letter-spacing: -0.01em;
            }
            .bc-tab:hover { color: #71717a; background: rgba(0,0,0,0.03); }
            .bc.secured  .bc-tab.active { color: #dc2626; background: rgba(220,38,38,0.09); }
            .bc.unlocked .bc-tab.active { color: #16a34a; background: rgba(22,163,74,0.09); }
            .bc.open     .bc-tab.active { color: #ca8a04; background: rgba(202,138,4,0.09); }
            .bc.offline  .bc-tab.active { color: #71717a; background: rgba(0,0,0,0.05); }

            /* ═══════════════════════════════════════════════════════
               CONTENT PANELS
            ═══════════════════════════════════════════════════════ */
            .bc-panel {
                // overflow-y: auto;
                display: none;
                scrollbar-width: thin;
                scrollbar-color: rgba(0,0,0,0.1) transparent;
            }
            .bc-panel.active { display: block; }
            .bc-panel::-webkit-scrollbar { width: 4px; }
            .bc-panel::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 2px; }
        </style>

        <div class="bc" data-bind="card">

            <!-- Header -->
            <div class="bc-hd">
                <div class="bc-hd-left">
                    <span data-bind="online-dot"></span>
                    <span class="bc-name" data-bind="device-name"></span>
                    <span class="bc-uuid" data-bind="device-uuid"></span>
                </div>
                <div data-container="view-context-menu"></div>
            </div>

            <!-- Hero -->
            <div class="bc-hero" data-bind="hero">
                <div class="bc-ghost bi" data-bind="ghost-icon"></div>
                <button class="bc-fab" data-bind="fab">
                    <i class="bi" data-bind="fab-icon"></i>
                </button>
                <div class="bc-state" data-bind="state-word"></div>
                <div class="bc-sub"   data-bind="state-sub"></div>
                <div class="bc-pips"  data-bind="pips"></div>
            </div>

            <!-- Tab nav -->
            <div class="bc-tabs">
                <button class="bc-tab active" data-tab="activity">Activity</button>
                <button class="bc-tab"        data-tab="details">Details</button>
                <button class="bc-tab"        data-tab="firmware">Firmware</button>
                <button class="bc-tab"        data-tab="logs">Logs</button>
            </div>

            <!-- Activity panel -->
            <div class="bc-panel active" data-panel="activity">
                <div data-container="activity-view"></div>
            </div>

            <!-- Details panel -->
            <div class="bc-panel" data-panel="details">
                <div data-container="details-view"></div>
            </div>

            <!-- Firmware panel -->
            <div class="bc-panel" data-panel="firmware">
                <div data-container="firmware-view"></div>
            </div>

            <!-- Logs panel -->
            <div class="bc-panel" data-panel="logs">
                <div data-container="logs-view"></div>
            </div>

        </div>
        `;
    }

    async onInit() {
        await this.model.fetch();
        // Activity view
        const historyCollection = new DeviceHistoryList({
            params: { device: this.model.get('id'), size: 5 }
        });
        this.historyView = new TableView({
            collection: historyCollection,
            containerId: 'activity-view',
            fetchOnMount: true,
            hideActivePillNames: ['device'],
            clickAction: 'none',
            fontSize: 'sm',
            columns: [
                {
                    key: 'created',
                    label: 'Event',
                    template: '{{model.created|relative_short}} {{{model.action|badge:lock_engaged=danger,lock_released=success,lock_right=danger,unlock_right=success,bin_full=warning,bin_emptied=success,fullness_changed=warning,latch_engaged=danger,latch_released=success,latch_reengaged=warning,bolt_opened=warning,bolt_closed=success,bolt_changed=warning,lock=danger,unlock=success}}} {{{model.source|badge}}} {{model.user}} {{model.metadata.lock}} {{model.metadata.bin}}',
                    sortable: true
                }
            ]
        });
        this.addChild(this.historyView);

        // Details view
        this.detailsView = new DataView({
            model: this.model,
            containerId: 'details-view',
            className: 'p-3',
            showEmptyValues: true,
            fields: [
                { name: 'name', label: 'Name', colSize: 6 },
                { name: 'uuid', label: 'Serial Number', colSize: 6 },
                { name: 'location', label: 'Location', colSize: 6 },
                { name: 'group.name', label: 'Group', colSize: 6 },
                { name: 'board', label: 'Board', colSize: 6 },
                // lock_version is device-reported (audit #293 H1): escape it. DataView renders formatter
                // output as raw HTML; nl2br escapes, and on this single-line value adds no markup.
                { name: 'lock_version', label: 'Lock Type', colSize: 6, formatter: "default('Unknown')|nl2br" },
                { name: 'metadata.version', label: 'Firmware', colSize: 6 },
                { name: 'metadata.kcv', label: 'Key (KCV)', colSize: 6 },
                { name: 'metadata.info.uptime', label: 'Uptime (s)', colSize: 6 },
                { name: 'metadata.info.free_heap', label: 'Free Memory', colSize: 6 },
                { name: 'last_seen', label: 'Last Seen', formatter: 'relative', colSize: 6 },
                { name: 'created', label: 'Created', formatter: 'datetime', colSize: 6 },
            ]
        });
        this.addChild(this.detailsView);

        // Firmware view
        this.firmwareView = new DeviceFirmwareView({
            model: this.model,
            containerId: 'firmware-view'
        });
        this.addChild(this.firmwareView);

        // Logs view
        const logsCollection = new LogList({
            params: { size: 5, model_name: 'camaccess.Device', model_id: this.model.get('id') }
        });
        this.logsView = new TableView({
            collection: logsCollection,
            containerId: 'logs-view',
            permissions: 'view_logs',
            fetchOnMount: true,
            fontSize: 'xs',
            hideActivePillNames: ['model_name', 'model_id'],
            columns: [
                { key: 'created', label: 'When', sortable: true, formatter: 'epoch|datetime' },
                { key: 'level', label: 'Level', sortable: true },
                { key: 'log', label: 'Log' }
            ]
        });
        this.addChild(this.logsView);

        // Context menu
        const contextMenu = new ContextMenu({
            containerId: 'view-context-menu',
            className: 'context-menu-view header-menu-absolute',
            context: this.model,
            config: {
                icon: 'bi-three-dots',
                items: [
                    { label: 'Refresh Status', action: 'refresh-status', icon: 'bi-arrow-clockwise' },
                    { type: 'divider' },
                    { label: 'Edit', action: 'edit', icon: 'bi-pencil' },
                    { label: 'Configure Lock', action: 'configure-lock', icon: 'bi-lock' },
                    { label: 'Configure BLE', action: 'configure-ble', icon: 'bi-bluetooth' },
                    { label: 'Configure WiFi', action: 'configure-wifi', icon: 'bi-wifi' },
                    { label: 'Configure WebSocket', action: 'configure-ws', icon: 'bi-plug' },
                    { type: 'divider' },
                    this.model.get('is_enabled')
                        ? { label: 'Disable Device', action: 'disable-device', icon: 'bi-x-circle' }
                        : { label: 'Enable Device', action: 'enable-device', icon: 'bi-check-circle' },
                ]
            }
        });
        this.addChild(contextMenu);

        // Realtime state change listener
        this._onDeviceStateChanged = (event) => {
            if (event.device_id === this.model.get('id')) {
                this.model.set('lock_engaged', event.lock_engaged);
                this.model.set('latch_opened', event.latch_opened);
                if (event.is_locked !== undefined) this.model.set('is_locked', event.is_locked);
                if (event.is_online !== undefined) this.model.set('is_online', event.is_online);
                if (event.board !== undefined) this.model.set('board', event.board);
                if (event.lock_version !== undefined) this.model.set('lock_version', event.lock_version);
                if (event.lock_right_engaged !== undefined) this.model.set('lock_right_engaged', event.lock_right_engaged);
                if (event.latch_right_opened !== undefined) this.model.set('latch_right_opened', event.latch_right_opened);
                if (event.bin_left_full !== undefined) this.model.set('bin_left_full', event.bin_left_full);
                if (event.bin_right_full !== undefined) this.model.set('bin_right_full', event.bin_right_full);
                if (event.bin_health !== undefined) this.model.set('bin_health', event.bin_health);
                this._fabPending = false;
                this._updateLockUI();
            }
        };
        this.getApp().events.on('device:state_changed', this._onDeviceStateChanged);

        // Config update listener — device replied to *_CONFIG commands
        this._onDeviceConfigUpdated = (event) => {
            if (event.device_id === this.model.get('id')) {
                const meta = { ...(this.model.get('metadata') || {}) };
                meta.device_config = { ...(meta.device_config || {}), [event.subsystem]: event.config };
                this.model.set('metadata', meta);
                this._updateLockUI();
            }
        };
        this.getApp().events.on('device:config_updated', this._onDeviceConfigUpdated);
    }

    async onAfterRender() {
        this._updateLockUI();
        this._bindFab();
        this._bindTabs();
        this._bindPips();

        // Auto-fetch config from device so connectivity pips populate
        if (this.model.get('is_online')) {
            try {
                await this.model.save({ configure: ['BLE_CONFIG', 'WIFI_CONFIG', 'WS_CONFIG', 'LOCK_CONFIG'] });
            } catch { /* best effort */ }
        }
    }

    _bindTabs() {
        const tabs = this.el?.querySelectorAll('.bc-tab');
        if (!tabs) return;
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                tabs.forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                const panels = this.el.querySelectorAll('.bc-panel');
                panels.forEach(p => p.classList.remove('active'));
                const panel = this.el.querySelector(`[data-panel="${tab.dataset.tab}"]`);
                if (panel) panel.classList.add('active');
            });
        });
    }

    _bindFab() {
        const fab = this._el('fab');
        if (!fab) return;
        fab.addEventListener('click', () => this._executeLockToggle());
    }

    _bindPips() {
        const pipsEl = this._el('pips');
        if (!pipsEl) return;
        pipsEl.addEventListener('click', (e) => {
            const pip = e.target.closest('[data-action]');
            if (!pip) return;
            const action = pip.dataset.action;
            if (action === 'configure-ble') this.onActionConfigureBle();
            else if (action === 'configure-wifi') this.onActionConfigureWifi();
        });
    }

    _updateLockUI() {
        const m = this.model.attributes || this.model;
        // Dual-lock (ACS101-SHARPS-A): trust board when reported; the non-null
        // right-lock fallback applies only while board is blank, so a
        // device re-flashed single-lock reverts to this path (and gets
        // its FAB back) once the corrected board arrives.
        const isDual = m.board === 'ACS101-SHARPS-A' || (!m.board && m.lock_right_engaged != null);
        if (isDual) {
            this._updateDualLockUI(m);
            return;
        }
        const lockEngaged = m.lock_engaged;
        const latchOpened = m.latch_opened;
        const online = m.is_online;
        const hasLatch = m.metadata?.config?.has_latch;
        const name = m.name || m.uuid || '';
        const uuid = m.uuid || '';

        // Compute display mode
        const isS100 = m.board === 'ACS101-E';
        const isLocked = isS100 ? !!m.is_locked : (hasLatch ? !latchOpened : lockEngaged);
        let mode;
        if (!online) {
            mode = 'offline';
        } else if (isS100) {
            // S100 securement is the server's is_locked (latch engaged AND bolt
            // closed); "open" = latch engaged but bolt ajar; else latch released.
            mode = isLocked ? 'secured' : (lockEngaged ? 'open' : 'unlocked');
        } else if (hasLatch && latchOpened) {
            mode = 'open';
        } else if (!isLocked) {
            mode = 'unlocked';
        } else {
            mode = 'secured';
        }

        // Card class
        const card = this._el('card');
        if (card) card.className = `bc ${mode}`;

        // Header
        this._setText('device-name', name);
        this._setText('device-uuid', uuid.slice(0, 14));
        const dotEl = this._el('online-dot');
        if (dotEl) dotEl.className = online ? 'bc-dot-on' : 'bc-dot-off';

        // Hero offline dimming
        const hero = this._el('hero');
        if (hero) hero.classList.toggle('off', !online);

        // Ghost icon
        const ghostIcons = {
            secured:  'bi-lock-fill',
            unlocked: 'bi-unlock-fill',
            open:     'bi-unlock-fill',
            offline:  'bi-lock-fill',
        };
        const ghostEl = this._el('ghost-icon');
        if (ghostEl) ghostEl.className = `bc-ghost bi ${ghostIcons[mode]}`;

        // State word
        const stateWords = { secured: 'Secured', unlocked: 'Unlocked', open: 'Open', offline: 'Offline' };
        this._setText('state-word', stateWords[mode]);

        // Sub-line
        const subLines = isS100 ? {
            secured:  'Latch engaged · bolt closed',
            unlocked: 'Latch released',
            open:     'Latch engaged · bolt open',
            offline:  'Device unreachable',
        } : {
            secured:  hasLatch ? 'Bolt engaged · latch closed' : 'Bolt engaged',
            unlocked: hasLatch ? 'Bolt released · latch closed' : 'Bolt released',
            open:     'Latch open · door ajar',
            offline:  'Device unreachable',
        };
        this._setText('state-sub', subLines[mode]);

        // FAB icon and state
        const fabIcons = { secured: 'bi-unlock', unlocked: 'bi-lock', open: 'bi-lock', offline: 'bi-wifi-off' };
        const fabIconEl = this._el('fab-icon');
        // S100 FAB toggles the latch, so its icon tracks lock_engaged (what
        // _executeLockToggle keys on), not `mode` — otherwise "open" (latch
        // engaged) would show a padlock whose tap actually unlocks.
        const fabIcon = isS100
            ? (!online ? 'bi-wifi-off' : (lockEngaged ? 'bi-unlock' : 'bi-lock'))
            : fabIcons[mode];
        if (fabIconEl) fabIconEl.className = `bi ${fabIcon}`;
        const fabEl = this._el('fab');
        if (fabEl) {
            // Un-hide in case a prior render ran the dual (ACS101-SHARPS-A) path.
            fabEl.style.display = '';
            fabEl.disabled = !online || this._fabPending;
            fabEl.style.opacity = this._fabPending ? '0.4' : '';
        }

        // Pips
        let pips = '';
        if (online) {
            if (isS100) {
                // S100: label pips to match the physical parts — the latch is
                // the commanded lock_engaged; the bolt is the latch_opened sensor.
                pips += `<span class="bc-pip ${lockEngaged ? 'bon' : 'boff'}"><span class="pd"></span> Latch</span>`;
                pips += `<span class="bc-pip ${latchOpened ? 'lo' : 'lc'}"><span class="pd"></span> Bolt</span>`;
            } else {
                pips += `<span class="bc-pip ${lockEngaged ? 'bon' : 'boff'}"><span class="pd"></span> Bolt</span>`;
                if (hasLatch) {
                    pips += `<span class="bc-pip ${latchOpened ? 'lo' : 'lc'}"><span class="pd"></span> Latch</span>`;
                }
            }

            // BLE status pip
            const ble = m.metadata?.device_config?.ble;
            if (ble) {
                const bleActive = ble.state === 'advertising' || ble.connected;
                const bleLabel = ble.connected ? 'BLE Connected' : ble.state === 'advertising' ? 'BLE' : 'BLE Off';
                pips += `<span class="bc-pip ${bleActive ? 'ble-on' : 'ble-off'}" data-action="configure-ble"><i class="bi bi-bluetooth" style="font-size:0.55rem"></i> ${bleLabel}</span>`;
            }

            // WiFi status pip
            const wifi = m.metadata?.device_config?.wifi;
            if (wifi) {
                const wifiActive = wifi.state === 'connected';
                const wifiLabel = wifiActive ? (wifi.ssid || 'WiFi') : 'WiFi Off';
                pips += `<span class="bc-pip ${wifiActive ? 'wifi-on' : 'wifi-off'}" data-action="configure-wifi"><i class="bi bi-wifi" style="font-size:0.55rem"></i> ${wifiLabel}</span>`;
            }
        }
        this._setHTML('pips', pips);
    }

    // ACS101-SHARPS-A dual-lock card: two bolts, two bins, no FAB (selector-less
    // remote actuation would error lock_required on-device).
    _updateDualLockUI(m) {
        const online = m.is_online;
        const name = m.name || m.uuid || '';
        const uuid = m.uuid || '';
        const leftEngaged = !!m.lock_engaged;
        const rightEngaged = !!m.lock_right_engaged;
        const anyLatchOpen = !!m.latch_opened || !!m.latch_right_opened;

        let mode;
        if (!online) {
            mode = 'offline';
        } else if (anyLatchOpen) {
            mode = 'open';
        } else if (leftEngaged && rightEngaged) {
            mode = 'secured';
        } else {
            mode = 'unlocked';
        }

        const card = this._el('card');
        if (card) card.className = `bc ${mode}`;

        this._setText('device-name', name);
        this._setText('device-uuid', uuid.slice(0, 14));
        const dotEl = this._el('online-dot');
        if (dotEl) dotEl.className = online ? 'bc-dot-on' : 'bc-dot-off';

        const hero = this._el('hero');
        if (hero) hero.classList.toggle('off', !online);

        const ghostIcons = {
            secured:  'bi-lock-fill',
            unlocked: 'bi-unlock-fill',
            open:     'bi-unlock-fill',
            offline:  'bi-lock-fill',
        };
        const ghostEl = this._el('ghost-icon');
        if (ghostEl) ghostEl.className = `bc-ghost bi ${ghostIcons[mode]}`;

        const stateWords = { secured: 'Secured', unlocked: 'Unlocked', open: 'Open', offline: 'Offline' };
        this._setText('state-word', stateWords[mode]);

        const sideWord = (engaged) => engaged ? 'engaged' : 'released';
        this._setText('state-sub', mode === 'offline'
            ? 'Device unreachable'
            : `L ${sideWord(leftEngaged)} · R ${sideWord(rightEngaged)}`);

        const fabEl = this._el('fab');
        if (fabEl) fabEl.style.display = 'none';

        let pips = '';
        if (online) {
            pips += `<span class="bc-pip ${leftEngaged ? 'bon' : 'boff'}"><span class="pd"></span> L Bolt</span>`;
            pips += `<span class="bc-pip ${rightEngaged ? 'bon' : 'boff'}"><span class="pd"></span> R Bolt</span>`;
            pips += this._binPip(m, 'left');
            pips += this._binPip(m, 'right');

            // BLE status pip
            const ble = m.metadata?.device_config?.ble;
            if (ble) {
                const bleActive = ble.state === 'advertising' || ble.connected;
                const bleLabel = ble.connected ? 'BLE Connected' : ble.state === 'advertising' ? 'BLE' : 'BLE Off';
                pips += `<span class="bc-pip ${bleActive ? 'ble-on' : 'ble-off'}" data-action="configure-ble"><i class="bi bi-bluetooth" style="font-size:0.55rem"></i> ${bleLabel}</span>`;
            }

            // WiFi status pip
            const wifi = m.metadata?.device_config?.wifi;
            if (wifi) {
                const wifiActive = wifi.state === 'connected';
                const wifiLabel = wifiActive ? (wifi.ssid || 'WiFi') : 'WiFi Off';
                pips += `<span class="bc-pip ${wifiActive ? 'wifi-on' : 'wifi-off'}" data-action="configure-wifi"><i class="bi bi-wifi" style="font-size:0.55rem"></i> ${wifiLabel}</span>`;
            }
        }
        this._setHTML('pips', pips);
    }

    // One bin pip: red when full, green when ok, amber ⚠ on a sensor
    // health caveat; nothing until the bin has ever reported.
    _binPip(m, side) {
        const full = side === 'left' ? m.bin_left_full : m.bin_right_full;
        if (full == null) return '';
        const label = side === 'left' ? 'L Bin' : 'R Bin';
        const health = m.bin_health?.[side] ?? m.metadata?.bins?.[side]?.health;
        if (health != null && health !== 'ok') {
            const title = String(health).replace(/"/g, '&quot;');
            return `<span class="bc-pip lo" title="${title}"><span class="pd"></span> ${label} ⚠</span>`;
        }
        return `<span class="bc-pip ${full ? 'bon' : 'boff'}"><span class="pd"></span> ${label}</span>`;
    }

    async _executeLockToggle() {
        if (!this.model.get('is_online') || this._fabPending) return;
        const locked = this.model.get('lock_engaged');
        const action = locked ? 'unlock' : 'lock';

        this._fabPending = true;
        this._updateLockUI();

        // Safety fallback: re-enable after 5s if no WS event arrives
        if (this._fabTimeout) clearTimeout(this._fabTimeout);
        this._fabTimeout = setTimeout(() => {
            this._fabPending = false;
            this._updateLockUI();
        }, 5000);

        try {
            const payload = locked ? { unlock: '5' } : { lock: true };
            const resp = await this.model.save(payload);
            if (!resp || !resp.success) {
                this.getApp().toast.error(resp?.message || `Failed to ${action}`);
                this._fabPending = false;
                this._updateLockUI();
            }
        } catch (err) {
            this.getApp().toast.error(`Failed to ${action}`);
            this._fabPending = false;
            this._updateLockUI();
        }
    }

    async onActionRefreshStatus() {
        try {
            await this.model.save({ get_status: true });
            await new Promise(r => setTimeout(r, 1500));
            await this.model.fetch();
            this._updateLockUI();
            this.getApp().toast.success('Status refreshed');
        } catch (err) {
            this.getApp().toast.error('Failed to refresh status');
        }
    }

    // Fetch fresh config from device, then show a subsystem config dialog.
    // Sends *_CONFIG (read) command, waits up to 4s for WS response, falls back to cached values.
    async _configureSubsystem(subsystem, title, fields) {
        const configCmd = `${subsystem.toUpperCase()}_CONFIG`;

        // Request fresh config from device
        try {
            await this.model.save({ configure: [configCmd] });
        } catch { /* best effort */ }

        // Wait for device:config_updated (or timeout with cached values)
        await new Promise(resolve => {
            const timeout = setTimeout(resolve, 4000);
            const handler = (event) => {
                if (event.device_id === this.model.get('id') && event.subsystem === subsystem) {
                    clearTimeout(timeout);
                    this.getApp().events.off('device:config_updated', handler);
                    resolve();
                }
            };
            this.getApp().events.on('device:config_updated', handler);
        });

        const origConfig = JSON.stringify(this.model.get('metadata')?.device_config?.[subsystem] || {});

        this.model.once('sync', async () => {
            const commands = this._configDiff(
                { [subsystem]: JSON.parse(origConfig) },
                { [subsystem]: this.model.get('metadata')?.device_config?.[subsystem] || {} }
            );
            if (commands.length) {
                try {
                    await this.model.save({ configure: commands });
                    this.getApp().toast.success(`Applied ${commands.length} setting${commands.length > 1 ? 's' : ''}`);
                } catch (err) {
                    this.getApp().toast.error('Failed to apply config');
                }
            }
        });

        Dialog.showModelForm({ model: this.model, title, fields });
    }

    async onActionConfigureLock() {
        await this._configureSubsystem('lock', 'Configure Lock', [
            { label: 'Trigger Mode', name: 'metadata.device_config.lock.trigger_mode', type: 'select', options: ['both', 'direct', 'relay'], columns: 6 },
            { label: 'Engage Duration (ms)', name: 'metadata.device_config.lock.engage_duration', type: 'number', columns: 6 },
            { label: 'Debounce (ms)', name: 'metadata.device_config.lock.debounce', type: 'number', columns: 6 },
            { label: 'Latch Events', name: 'metadata.device_config.lock.events', type: 'checkbox', columns: 6 },
        ]);
    }

    async onActionConfigureBle() {
        await this._configureSubsystem('ble', 'Configure BLE', [
            { label: 'Start on Boot', name: 'metadata.device_config.ble.boot', type: 'checkbox', columns: 6 },
            { label: 'Mode', name: 'metadata.device_config.ble.mode', type: 'select', options: ['connectable', 'beacon'], columns: 6 },
            { label: 'Device Name', name: 'metadata.device_config.ble.name', columns: 6 },
            { label: 'TX Power (dBm)', name: 'metadata.device_config.ble.power', type: 'number', columns: 6 },
        ]);
    }

    async onActionConfigureWifi() {
        await this._configureSubsystem('wifi', 'Configure WiFi', [
            { label: 'Start on Boot', name: 'metadata.device_config.wifi.boot', type: 'checkbox', columns: 6 },
            { label: 'Auto Connect', name: 'metadata.device_config.wifi.auto_connect', type: 'checkbox', columns: 6 },
            { label: 'SSID', name: 'metadata.device_config.wifi.ssid', columns: 6 },
            { label: 'Password', name: 'metadata.device_config.wifi.password', type: 'password', columns: 6 },
        ]);
    }

    async onActionConfigureWs() {
        await this._configureSubsystem('ws', 'Configure WebSocket', [
            { label: 'URL', name: 'metadata.device_config.ws.url', columns: 12 },
        ]);
    }

    // Returns a list of "SUBSYSTEM_CONFIG key val" strings for values that changed.
    _configDiff(orig, updated) {
        const commands = [];
        for (const sub of ['lock', 'ble', 'wifi', 'ws']) {
            const oldSub = orig[sub] || {};
            const newSub = updated[sub] || {};
            for (const [key, val] of Object.entries(newSub)) {
                // Never send an empty password — skip if blank
                if (sub === 'wifi' && key === 'password' && !val) continue;
                if (String(val) !== String(oldSub[key] ?? '')) {
                    commands.push(`${sub.toUpperCase()}_CONFIG ${key} ${val}`);
                }
            }
        }
        return commands;
    }

    async onActionEdit() {
        Dialog.showModelForm({
            model: this.model,
            fields: [
                { label: 'Name', name: 'name', columns: 12 },
                { label: 'Location', name: 'location', columns: 12 },
            ]
        });
    }

    async onActionEnableDevice() {
        const confirmed = await Dialog.confirm({ title: 'Enable Device', message: 'Enable this device?' });
        if (!confirmed) return;
        const resp = await this.model.save({ is_enabled: true });
        if (resp?.success) this.getApp().toast.success('Device enabled');
        else this.getApp().toast.error('Failed to enable device');
    }

    async onActionDisableDevice() {
        const confirmed = await Dialog.confirm({ title: 'Disable Device', message: 'Disable this device?' });
        if (!confirmed) return;
        const resp = await this.model.save({ is_enabled: false });
        if (resp?.success) this.getApp().toast.success('Device disabled');
        else this.getApp().toast.error('Failed to disable device');
    }

    // Helpers
    _el(bind)            { return this.el?.querySelector(`[data-bind="${bind}"]`); }
    _setText(bind, text) { const el = this._el(bind); if (el) el.textContent = text || ''; }
    _setHTML(bind, html) { const el = this._el(bind); if (el) el.innerHTML = html || ''; }

    _onModelChange() {}

    onBeforeDestroy() {
        if (this._onDeviceStateChanged) {
            this.getApp().events.off('device:state_changed', this._onDeviceStateChanged);
        }
        if (this._onDeviceConfigUpdated) {
            this.getApp().events.off('device:config_updated', this._onDeviceConfigUpdated);
        }
        if (this._fabTimeout) clearTimeout(this._fabTimeout);
    }

    static create(options = {}) {
        return new DeviceView(options);
    }
}

Device.VIEW_CLASS = DeviceView;
export default DeviceView;
